import { useState, type ReactElement } from 'react';
import type { PostSummaryDto } from '@gr/core/content/posts/dto';
import { BlogPermission } from '@gr/core/accounts/workspaces/permissions';
import { useConfig, useNavigate, useSearchParams, useServices, useT } from '../../../app/di';
import { arrayPaging, usePagedResource, useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button, ShowMoreButton } from '../../../ui-kit/Button/Button';
import { Dialog } from '../../../ui-kit/Dialog/Dialog';
import { EmptyState } from '../../../ui-kit/EmptyState/EmptyState';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { PageHeader } from '../../../ui-kit/PageHeader/PageHeader';
import { useBlog } from '../../accounts/workspaces';
import { CreatePostForm } from './CreatePostForm/CreatePostForm';
import { EMPTY_POST_FILTERS, isFiltered, listQueryOf } from './model/post-filters';
import { PostCard } from './PostCard/PostCard';
import { PostFilters } from './PostFilters/PostFilters';
import styles from './PostsPage.module.scss';

const POST_PAGING = arrayPaging<PostSummaryDto>();

// POST-UC-1, POST-UC-6
export function PostsPage(): ReactElement {
  const { api, router } = useServices();
  const { t } = useT();
  const { blog, can } = useBlog();
  const search = useSearchParams();
  const linkedTo = search.get('linkedTo') ?? undefined;
  const [filters, setFilters] = useState(EMPTY_POST_FILTERS);
  const [creating, setCreating] = useState(false);
  const statuses = useResource(() => api.posts.statuses(blog.id), [api, blog.id]);
  const formats = useResource(() => api.posts.formats(blog.id), [api, blog.id]);
  const channels = useResource(() => api.channels.list(blog.id), [api, blog.id]);
  const { ui } = useConfig();
  const posts = usePagedResource((page) => api.posts.list(blog.id, { ...listQueryOf(filters, linkedTo), ...page }), [api, blog.id, filters, linkedTo], POST_PAGING, ui.lists.pageSize);
  const filtered = isFiltered(filters, linkedTo);
  const channelName = (id: string): string => channels.data?.find((c) => c.id === id)?.name ?? '';
  const create = can(BlogPermission.EditContent) ? (
    <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
      {t('posts.list.create')}
    </Button>
  ) : null;

  return (
    <div className="u-stack-lg">
      <PageHeader title={t('posts.list.title')} actions={create} />
      <PostFilters filters={filters} statuses={statuses.data ?? []} formats={formats.data ?? []} onChange={setFilters} />
      {linkedTo ? (
        <Alert tone="info">
          <span className="u-row">
            {t('posts.list.linkedTo')}
            <Button size="sm" variant="ghost" onClick={() => router.navigate(router.path('posts', { blogId: blog.id }))}>
              {t('posts.list.clearLinked')}
            </Button>
          </span>
        </Alert>
      ) : null}
      {posts.loading && !posts.data ? (
        <Loading label={t('common.loading')} />
      ) : posts.error ? (
        <Alert tone="danger" title={t('common.loadFailed')}>
          <Button size="sm" onClick={() => void posts.reload()}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : !posts.data || posts.data.length === 0 ? (
        <EmptyState title={filtered ? t('posts.list.emptySearch') : t('posts.list.empty')} text={filtered ? undefined : t('posts.list.emptyText')} action={filtered ? undefined : create} />
      ) : (
        <ul className={styles.list}>
          {posts.data.map((post) => (
            <li key={post.id}>
              <PostCard
                post={post}
                status={statuses.data?.find((s) => s.id === post.statusId)}
                publishedIn={post.publishedChannelIds.map(channelName)}
                timeZone={blog.timezone}
                sortedBy={filters.sort}
                settings={ui.posts}
              />
            </li>
          ))}
        </ul>
      )}
      <ShowMoreButton visible={posts.hasMore && !posts.error} loading={posts.loadingMore} onClick={() => void posts.loadMore()}>
        {t('common.showMore')}
      </ShowMoreButton>
      {creating ? <CreatePostDialog onClose={() => setCreating(false)} /> : null}
    </div>
  );
}

function CreatePostDialog({ onClose }: { onClose(): void }): ReactElement {
  const { api } = useServices();
  const { t } = useT();
  const { blog } = useBlog();
  const navigate = useNavigate();
  return (
    <Dialog
      open
      title={t('posts.list.create')}
      closeLabel={t('common.close')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="create-post" variant="primary">
            {t('common.create')}
          </Button>
        </>
      }
    >
      <CreatePostForm
        id="create-post"
        onSubmit={async (title) => {
          const result = await api.posts.create(blog.id, { title });
          if (result.success) navigate('post', { blogId: blog.id, postId: result.data.id });
          return result;
        }}
      />
    </Dialog>
  );
}
